// IndexedDB-backed queue for offline photo captures.
// Stores pending uploads keyed by auto-increment id; drained on reconnect.

const DB_NAME = "ssc-queue";
const STORE = "uploads";
const VERSION = 1;

let dbPromise = null;

const openDB = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
};

const run = (mode, fn) =>
  new Promise(async (resolve, reject) => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      let result;
      const maybeReq = fn(store);
      if (maybeReq && "onsuccess" in maybeReq) {
        maybeReq.onsuccess = () => {
          result = maybeReq.result;
        };
        maybeReq.onerror = () => reject(maybeReq.error);
      }
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    } catch (e) {
      reject(e);
    }
  });

export const Queue = {
  add: (item) => run("readwrite", (s) => s.add({ ...item, ts: Date.now() })),
  list: () => run("readonly", (s) => s.getAll()),
  count: () => run("readonly", (s) => s.count()),
  remove: (id) => run("readwrite", (s) => s.delete(id)),
  clear: () => run("readwrite", (s) => s.clear()),
};

// Simple pub/sub so UI can re-render on queue changes.
const listeners = new Set();
export const onQueueChange = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const notify = async () => {
  try {
    const c = await Queue.count();
    listeners.forEach((cb) => cb(c));
  } catch { /* noop */ }
};

export const enqueueCapture = async (sessionId, payload) => {
  await Queue.add({ sessionId, payload });
  notify();
};

/**
 * Drain the queue by retrying each pending upload via the given uploader.
 * @param {(sessionId: string, payload: object) => Promise<any>} uploader
 * @param {(status: {sent: number, total: number, current?: string}) => void} onProgress
 * @returns {Promise<{sent: number, failed: number, total: number}>}
 */
export const drainQueue = async (uploader, onProgress) => {
  const items = await Queue.list();
  let sent = 0;
  let failed = 0;
  const total = items.length;
  for (const it of items) {
    onProgress?.({ sent, total, current: it.payload?.filename });
    try {
      await uploader(it.sessionId, it.payload);
      await Queue.remove(it.id);
      sent += 1;
    } catch {
      failed += 1;
      // Stop on first failure; the user is likely still offline.
      break;
    }
  }
  notify();
  return { sent, failed, total };
};

export const initialQueueCount = () => Queue.count().catch(() => 0);
